import {sendEvent} from '@github-ui/hydro-analytics'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {SectionHeader} from '@github-ui/issue-metadata/SectionHeader'
import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import {
  type CodeScanningAlert,
  CodeScanningAlertPicker,
  type CodeScanningAlertStatus,
} from '@github-ui/item-picker-alert/CodeScanningAlertPicker'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useAlert} from '@github-ui/sub-issues/useAlert'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useRemoveSubIssueMutation} from '@github-ui/sub-issues/useSubIssueMembershipMutations'
import {CommandActionListItem, type CommandActionListItemProps, GlobalCommands} from '@github-ui/ui-commands'
import {LockIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, CounterLabel} from '@primer/react'
import type React from 'react'
import {type HTMLAttributes, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {LABELS} from '../../../constants/labels'
import {useAlertTrackingErrors} from '../../../hooks/use-alert-tracking-errors'
import {useDependencyAlerts} from '../../../hooks/use-dependencies-alert'
import {useSetParentMutation} from '../../../mutations/use-set-parent-mutation'
import {useIssueHeaderDataContext} from '../../header/IssueHeaderDataContext'
import type {IssueSecondaryNode} from '../../issue-viewer-secondary-query-types'
import {RelationshipsAlertDialog} from '../../shared/RelationshipsAlertDialog'
import {DependenciesPickerLoader, type DependenciesPickerProps} from './DependenciesPicker'
import {DependencyIssue} from './DependencyIssue'
import {blockedByRelationshipsQueryKey, blockingRelationshipsQueryKey} from './fetch-relationships'
import {fetchSecurityAlerts, type SecurityAlertNode, securityAlertsQueryKey} from './fetch-security-alerts'
import {LazyRelationshipsBlockedByListView} from './LazyRelationshipsBlockedByListView'
import {LazyRelationshipsBlockingListView} from './LazyRelationshipsBlockingListView'
import {ParentIssue} from './ParentIssue'
import {RelatesToGroup} from './RelatesToGroup'
import styles from './RelationshipsSection.module.css'
import {RelationshipsSecurityAlertsListView} from './RelationshipsSecurityAlertsListView'
import {RelationshipsViewAllButton} from './RelationshipsViewAllButton'
import {SecurityAlert} from './SecurityAlert'
import {useResetOnEnter} from './use-reset-on-enter'

const VISIBLE_ISSUES_THRESHOLD = 3
const VISIBLE_ALERTS_THRESHOLD = 3
type DependenciesPickerState = DependenciesPickerProps['type'] | null

// The fields `RelationshipsSectionInternal` reads off an `Issue`, derived from the allowlisted
// `IssueViewerSecondaryViewQuery` types. `repository.owner` is narrowed to `{login}` since that's all
// this component reads.
export type RelationshipsSectionFragment = Pick<
  IssueSecondaryNode,
  | 'id'
  | 'number'
  | 'title'
  | 'databaseId'
  | 'viewerCanUpdateMetadata'
  | 'parent'
  | 'topBlockedBy'
  | 'topBlocking'
  | 'topRelatesTo'
  | 'securityAlerts'
  | 'issueDependenciesSummary'
  | 'state'
> & {
  readonly repository: Pick<IssueSecondaryNode['repository'], 'nameWithOwner' | 'isArchived'> & {
    readonly owner: {readonly login: string}
  }
}

type RelationshipsSectionInternalProps = {
  issue: RelationshipsSectionFragment
  onLinkClick?: (event: MouseEvent) => void
  onSelection: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

type RelationshipActionListItemProps = Pick<CommandActionListItemProps, 'children' | 'commandId'> & {
  onSelect: () => void
  shouldAcceptCommand: boolean
}

function RelationshipActionListItem({
  children,
  commandId,
  onSelect,
  shouldAcceptCommand,
}: RelationshipActionListItemProps) {
  return shouldAcceptCommand ? (
    <CommandActionListItem commandId={commandId}>{children}</CommandActionListItem>
  ) : (
    <ActionList.Item onSelect={onSelect}>{children}</ActionList.Item>
  )
}

function checkForInaccessibleIssues(totalIssuesCount: number, visibleIssuesCount: number, isLastPage: boolean) {
  // if we have more total issues than what's visible then we have hidden issues.
  const hasHiddenIssues = totalIssuesCount > visibleIssuesCount

  // if fewer than 3 issues are visible, it means there are hidden/private issues.
  // if exactly 3 issues are visible (which is the maximum per page), and there's no next page of results, it also indicates that some issues are private.
  const issuesAreHiddenDueToPrivacy =
    visibleIssuesCount < VISIBLE_ISSUES_THRESHOLD || (visibleIssuesCount === VISIBLE_ISSUES_THRESHOLD && isLastPage)

  return hasHiddenIssues && issuesAreHiddenDueToPrivacy
}

export function RelationshipsSectionFallback() {
  return (
    <Section
      sectionHeader={<ReadonlySectionHeader title={LABELS.sectionTitles.relationships} />}
      emptyText={LABELS.emptySections.relationships}
    />
  )
}

function parseNameWithOwner(value: string) {
  const [owner, repo, ...extra] = value.split('/')

  if (!owner || !repo || extra.length > 0) {
    throw new Error('Invalid repository nameWithOwner')
  }

  return {owner, repo}
}

export function RelationshipsSectionInternal({
  issue: fragmentIssue,
  onLinkClick,
  insideSidePanel,
  readonly,
  onSelection,
}: RelationshipsSectionInternalProps) {
  const headerIssue = useIssueHeaderDataContext()
  const {
    parent,
    topBlockedBy,
    topBlocking,
    topRelatesTo,
    repository,
    id,
    number,
    title,
    issueDependenciesSummary,
    state,
  } = headerIssue
  const {databaseId, securityAlerts: propSecurityAlerts} = fragmentIssue

  const queryClient = useQueryClient()
  const {owner, repo} = parseNameWithOwner(repository.nameWithOwner)
  const parentNameWithOwner = parent?.repository?.nameWithOwner
  let parentRepository: ReturnType<typeof parseNameWithOwner> | undefined
  if (parentNameWithOwner) {
    try {
      parentRepository = parseNameWithOwner(parentNameWithOwner)
    } catch {
      parentRepository = undefined
    }
  }
  const {removeSubIssue: removeParent} = useRemoveSubIssueMutation({
    owner: parentRepository?.owner,
    repo: parentRepository?.repo,
    number: parent?.number,
    parentIssueId: parent?.id ?? id,
  })

  // `topRelatesTo.totalCount` is the full accessible total, so it stays correct even though the preview is capped at `first: 3`.
  const relatesToCount = topRelatesTo?.totalCount ?? 0

  const initialAlerts = (propSecurityAlerts?.nodes?.filter(n => n != null) as SecurityAlertNode[]) ?? []

  // Seeded from props; only refetches when we call `invalidateQueries` after the alert picker PATCH.
  const {data: fetchedAlerts} = useQuery({
    queryKey: securityAlertsQueryKey(owner, repo, number),
    queryFn: ({signal}) => fetchSecurityAlerts(owner, repo, number, signal),
    initialData: initialAlerts,
    staleTime: Infinity,
    enabled: !!owner && !!repo && !!number,
  })

  const securityAlerts = useMemo(() => ({nodes: fetchedAlerts}), [fetchedAlerts])
  const {alert, setAlert, resetAlert, showServerAlert} = useAlert()
  const setParent = useSetParentMutation({
    issueId: id,
    issueTitle: title ?? '',
    issueNumber: number,
    issueDatabaseId: databaseId ?? null,
    issueState: state,
    nameWithOwner: repository.nameWithOwner,
    currentParentId: parent?.id ?? undefined,
    owner,
    repo,
    onCompleted: onSelection,
    onError: showServerAlert,
  })
  const {
    alerts: dependencyAlerts,
    resetAlerts: resetDependencyAlerts,
    setAlertsFromErrors: setDependencyAlertsFromErrors,
  } = useDependencyAlerts()
  const {
    errors: alertTrackingErrors,
    resetErrors: resetAlertTrackingErrors,
    setErrors: setAlertTrackingErrors,
  } = useAlertTrackingErrors()
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  // Picker states reset whenever the menu transitions from closed to open. Each
  // reset is exposed in the same render so the JSX below sees the cleared state
  // without an extra commit.
  const [pickerType, setPickerType] = useResetOnEnter<'Issue' | 'Repository' | null>(null, menuOpen)
  const [dependenciesPickerState, setDependenciesPickerState] = useResetOnEnter<DependenciesPickerState>(null, menuOpen)
  const [alertPickerOpen, setAlertPickerOpen] = useResetOnEnter<boolean>(false, menuOpen)
  // Shared gate for the button and the keyboard shortcuts so they can't drift. Despite its name,
  // `useCanEditSubIssues` maps to `viewerCanUpdateMetadata` — the triage floor every relationship mutation requires.
  const canEditRelationships = useCanEditSubIssues(fragmentIssue) && !readonly

  const onDependenciesPickerClose = useCallback(() => {
    setDependenciesPickerState(null)
  }, [setDependenciesPickerState])

  /**
   * When the issue-viewer is rendered within the side-panel, two issue-viewers are rendered at a time. This results
   * in `<GlobalCommands>` activating both pickers at once. To remedy this, we check if the side-panel is open and
   * whether or not the current issue-viewer is within the side-panel.
   *
   * If insideSidePanel is false, the issue-viewer is not inside the open side-panel.
   * If insideSidePanel is undefined, the side panel is not open.
   */
  const shouldAcceptCommand = insideSidePanel || insideSidePanel === undefined

  const openParentPicker = useCallback(() => {
    setMenuOpen(false)
    setPickerType('Issue')
  }, [setPickerType])

  const openBlockedByPicker = useCallback(() => {
    setMenuOpen(false)
    setDependenciesPickerState('blockedBy')
  }, [setDependenciesPickerState])

  const openBlockingPicker = useCallback(() => {
    setMenuOpen(false)
    setDependenciesPickerState('blocking')
  }, [setDependenciesPickerState])

  const openRelatesToPicker = useCallback(() => {
    setMenuOpen(false)
    setDependenciesPickerState('relatesTo')
  }, [setDependenciesPickerState])

  const openSecurityAlertPicker = useCallback(() => {
    setMenuOpen(false)
    setAlertPickerOpen(true)
  }, [setAlertPickerOpen])

  const onIssueSelection = useCallback(
    (selectedIssue: IssuePickerItem[]) => {
      if (!selectedIssue[0]) {
        // No selected issue means that an issue was deselected, so we remove the parent if there is one
        if (parent) {
          const removeCurrentParent = async () => {
            try {
              await removeParent({subIssueId: id, closed: state === 'CLOSED'})
              onSelection()
            } catch (error) {
              const parentRemovalError = error instanceof Error ? error : new Error('Failed to remove parent issue')
              if (!showServerAlert(parentRemovalError)) {
                setAlert({title: 'Unable to remove parent issue', body: parentRemovalError.message})
              }
            }
          }
          void removeCurrentParent()
        }
      } else {
        setParent(selectedIssue[0])
      }
    },
    [id, onSelection, parent, removeParent, setAlert, setParent, showServerAlert, state],
  )

  const onPickerTypeChange = useCallback((t: 'Issue' | 'Repository' | null) => setPickerType(t), [setPickerType])

  const hasBlockedBy = topBlockedBy?.nodes && topBlockedBy.nodes.length > 0
  const hasBlocking = topBlocking?.nodes && topBlocking.nodes.length > 0
  const hasRelatesTo = relatesToCount > 0
  const hasSecurityAlerts = securityAlerts?.nodes && securityAlerts.nodes.length > 0

  const hasViewAllBlockedBy = topBlockedBy?.pageInfo?.hasNextPage
  const hasViewAllBlocking = topBlocking?.pageInfo?.hasNextPage
  const hasViewAllSecurityAlerts = (securityAlerts?.nodes?.length ?? 0) > VISIBLE_ALERTS_THRESHOLD

  const hasInaccessibleBlockedByIssues = useMemo(() => {
    const totalBlockedByCount = issueDependenciesSummary.blockedBy || 0
    const visibleIssuesCount = topBlockedBy?.nodes?.length || 0
    const isLastPage = !topBlockedBy?.pageInfo?.hasNextPage

    return checkForInaccessibleIssues(totalBlockedByCount, visibleIssuesCount, isLastPage)
  }, [issueDependenciesSummary, topBlockedBy])

  const hasInaccessibleBlockingIssues = useMemo(() => {
    const totalBlockingCount = issueDependenciesSummary.blocking || 0
    const visibleIssuesCount = topBlocking?.nodes?.length || 0
    const isLastPage = !topBlocking?.pageInfo?.hasNextPage

    return checkForInaccessibleIssues(totalBlockingCount, visibleIssuesCount, isLastPage)
  }, [issueDependenciesSummary, topBlocking])

  // parent + blockedBy/blocking (accessible or inaccessible). Anything rendered above the relates-to group,
  // used both to divide the relates-to group from what precedes it and as the base for the security divider.
  const hasBlockingRelationships = Boolean(
    parent || hasBlockedBy || hasInaccessibleBlockedByIssues || hasBlocking || hasInaccessibleBlockingIssues,
  )

  const hasDependencies =
    hasBlockedBy ||
    hasBlocking ||
    hasRelatesTo ||
    hasSecurityAlerts ||
    hasInaccessibleBlockedByIssues ||
    hasInaccessibleBlockingIssues
  const showDividerForSecurityAlerts = hasBlockingRelationships || hasRelatesTo

  const hasContent = useMemo(() => {
    return parent || hasDependencies
  }, [parent, hasDependencies])

  const sendShownEvent = useEffectEvent(() => {
    if (hasDependencies) {
      sendEvent('issue_viewer.dependencies.shown')
    }
  })
  useEffect(() => {
    // we only want to send the event once on initial render
    sendShownEvent()
  }, [])

  // As currently designed, the ItemPicker requires an element to render as the anchor. This gets eventually
  // passed down to the SelectPanel which will handle rendering this element. In reality though, we don't want
  // this component to be responsible for rendering an element, we just want it to attach to the ref to an
  // existing anchor, so we do that here and return an empty element to "render"
  const sectionHeaderRef = useRef<HTMLButtonElement | null>(null)
  const itemPickerAnchorElement = (props: HTMLAttributes<HTMLElement>) => {
    const {ref} = props as {
      ref: React.RefObject<HTMLButtonElement | null>
    }
    if (ref) {
      ref.current = sectionHeaderRef.current
    }
    // We don't actually want to render any element, as we are relying on the rendered section header
    return <></>
  }

  const initialAlertSelection: CodeScanningAlert[] = useMemo(() => {
    return (
      securityAlerts?.nodes?.map(a => ({
        number: a?.number || 0,
        repository: {
          nameWithOwner: a?.repository?.nameWithOwner || '',
        },
        state: a?.status as CodeScanningAlertStatus,
        title: a?.title || '',
      })) ?? []
    )
  }, [securityAlerts])

  const onAlertSelection = useCallback(() => {
    void queryClient.invalidateQueries({queryKey: securityAlertsQueryKey(owner, repo, number)})
    onSelection()
  }, [queryClient, owner, repo, number, onSelection])

  const onDependencySelection = useCallback(() => {
    void queryClient.invalidateQueries({queryKey: blockingRelationshipsQueryKey(owner, repo, number)})
    void queryClient.invalidateQueries({queryKey: blockedByRelationshipsQueryKey(owner, repo, number)})
    onSelection()
  }, [queryClient, owner, repo, number, onSelection])

  return (
    <Section
      id="sidebar-relationships-section"
      emptyText={hasContent ? undefined : LABELS.emptySections.relationships}
      sectionHeader={
        <>
          {shouldAcceptCommand && canEditRelationships && (
            <GlobalCommands
              commands={{
                'issue-viewer:edit-parent': openParentPicker,
                'issue-viewer:mark-blocked-by': openBlockedByPicker,
                'issue-viewer:mark-blocking': openBlockingPicker,
                'issue-viewer:mark-relates-to': openRelatesToPicker,
                'issue-viewer:edit-security-alert': openSecurityAlertPicker,
              }}
            />
          )}
          {/* We render the section header separate of both the picker and the action menu that will anchor to it.
              we set up a ref for those other elements to use to anchor to instead */}
          <SectionHeader
            ref={sectionHeaderRef}
            buttonProps={{
              onClick: () => setMenuOpen(o => !o),
            }}
            readonly={!canEditRelationships}
            title={LABELS.sectionTitles.relationships}
          />
          <RepositoryAndIssuePicker
            onPickerTypeChange={onPickerTypeChange}
            selectedIssueIds={parent ? [parent.id] : []}
            hiddenIssueIds={[id]}
            onIssueSelection={onIssueSelection}
            organization={repository.owner.login}
            defaultRepositoryNameWithOwner={repository.nameWithOwner}
            pickerType={pickerType}
            anchorElement={itemPickerAnchorElement}
          />
          {dependenciesPickerState && (
            <DependenciesPickerLoader
              type={dependenciesPickerState}
              onClose={onDependenciesPickerClose}
              issueId={id}
              issueNumber={number}
              issueTitle={title ?? ''}
              organization={repository.owner.login}
              defaultRepositoryNameWithOwner={repository.nameWithOwner}
              anchorRef={sectionHeaderRef}
              onError={setDependencyAlertsFromErrors}
              onSelection={onDependencySelection}
            />
          )}
          {alertPickerOpen && databaseId != null && (
            <CodeScanningAlertPicker
              anchorElement={itemPickerAnchorElement}
              selectedAlerts={initialAlertSelection}
              onSelection={onAlertSelection}
              onError={setAlertTrackingErrors}
              initialOwner={repository.owner.login}
              initialRepo={repository.nameWithOwner.slice(repository.owner.login.length + 1)}
              issueId={databaseId}
            />
          )}
          {dependencyAlerts.length > 0 && (
            <RelationshipsAlertDialog
              title={`Failed to save ${dependencyAlerts.length} ${dependencyAlerts.length > 1 ? 'issues' : 'issue'}`}
              onClose={resetDependencyAlerts}
              width="large"
              data-testid="dependency-alert-dialog"
            >
              {dependencyAlerts.map((dependencyAlert, i) => (
                <Banner
                  // Index is unfortunately the only unique property we have of this alert
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={i}
                  variant="critical"
                  className={styles.DependencyErrorBanner}
                  aria-label={dependencyAlert.title}
                >
                  <Banner.Title>{dependencyAlert.title}</Banner.Title>
                  <Banner.Description>{dependencyAlert.body}</Banner.Description>
                </Banner>
              ))}
            </RelationshipsAlertDialog>
          )}
          {alertTrackingErrors.length > 0 && (
            <RelationshipsAlertDialog
              title={`Failed to save ${alertTrackingErrors.length} ${alertTrackingErrors.length > 1 ? 'alerts' : 'alert'}`}
              onClose={resetAlertTrackingErrors}
              width="large"
              data-testid="alert-tracking-error-dialog"
            >
              {alertTrackingErrors.map((alertTrackingError, i) => (
                <Banner
                  // Index is unfortunately the only unique property we have of this alert
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={i}
                  variant="critical"
                  className={styles.AlertTrackingErrorBanner}
                  aria-label={alertTrackingError.title}
                >
                  <Banner.Title>{alertTrackingError.title}</Banner.Title>
                  <Banner.Description>{alertTrackingError.body}</Banner.Description>
                </Banner>
              ))}
            </RelationshipsAlertDialog>
          )}
          <ActionMenu open={menuOpen} onOpenChange={open => setMenuOpen(open)} anchorRef={sectionHeaderRef}>
            <ActionMenu.Overlay width="medium">
              <ActionList>
                <ActionList.Group>
                  <RelationshipActionListItem
                    commandId="issue-viewer:edit-parent"
                    onSelect={openParentPicker}
                    shouldAcceptCommand={shouldAcceptCommand}
                  >
                    {parent ? 'Change or remove parent' : 'Add parent'}
                  </RelationshipActionListItem>
                  <RelationshipActionListItem
                    commandId="issue-viewer:mark-blocked-by"
                    onSelect={openBlockedByPicker}
                    shouldAcceptCommand={shouldAcceptCommand}
                  >
                    {hasBlockedBy ? 'Add or change blocked by' : 'Mark as blocked by'}
                  </RelationshipActionListItem>
                  <RelationshipActionListItem
                    commandId="issue-viewer:mark-blocking"
                    onSelect={openBlockingPicker}
                    shouldAcceptCommand={shouldAcceptCommand}
                  >
                    {hasBlocking ? 'Add or change blocking' : 'Mark as blocking'}
                  </RelationshipActionListItem>
                  <RelationshipActionListItem
                    commandId="issue-viewer:mark-relates-to"
                    onSelect={openRelatesToPicker}
                    shouldAcceptCommand={shouldAcceptCommand}
                  >
                    {hasRelatesTo ? LABELS.relatesToActions.addOrChange : LABELS.relatesToActions.add}
                  </RelationshipActionListItem>
                  <RelationshipActionListItem
                    commandId="issue-viewer:edit-security-alert"
                    onSelect={openSecurityAlertPicker}
                    shouldAcceptCommand={shouldAcceptCommand}
                  >
                    {hasSecurityAlerts ? 'Change or add security alerts' : 'Add security alert'}
                  </RelationshipActionListItem>
                </ActionList.Group>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </>
      }
    >
      {alert && (
        <RelationshipsAlertDialog title={alert.title} onClose={resetAlert}>
          {alert.body}
        </RelationshipsAlertDialog>
      )}
      <ActionList variant="full" className={styles.ActionList_Relationship_Overrides}>
        {parent && (
          <ActionList.Group>
            <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
              {LABELS.relationNames.parentIssue}
            </ActionList.GroupHeading>
            <ParentIssue onLinkClick={onLinkClick} issue={parent} />
          </ActionList.Group>
        )}
        {(hasBlockedBy || hasInaccessibleBlockedByIssues) && (
          <>
            {parent && <ActionList.Divider className={styles.ActionList_Divider} />}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
                {LABELS.relationNames.blockedByIssues}
                {issueDependenciesSummary.blockedBy > 0 && (
                  <CounterLabel className={styles.RelationshipCountLabel} variant="secondary">
                    {issueDependenciesSummary.blockedBy}
                  </CounterLabel>
                )}
              </ActionList.GroupHeading>
              {hasBlockedBy &&
                topBlockedBy.nodes.map(blockedIssue =>
                  blockedIssue ? (
                    <DependencyIssue key={blockedIssue.id} onLinkClick={onLinkClick} issue={blockedIssue} />
                  ) : null,
                )}
              {hasInaccessibleBlockedByIssues && (
                <ActionList.Item disabled aria-disabled="true">
                  <ActionList.LeadingVisual>
                    <LockIcon size={16} />
                  </ActionList.LeadingVisual>
                  <span className="text-small fgColor-muted" data-testid="private-blocked-by-notice">
                    Private issues are hidden.
                  </span>
                </ActionList.Item>
              )}
              {hasViewAllBlockedBy && (
                <RelationshipsViewAllButton
                  dialogTitle={LABELS.relationNames.blockedByIssues}
                  count={issueDependenciesSummary.blockedBy}
                >
                  <LazyRelationshipsBlockedByListView
                    owner={owner}
                    repo={repo}
                    issueNumber={number}
                    blockedByCount={issueDependenciesSummary.blockedBy}
                  />
                </RelationshipsViewAllButton>
              )}
            </ActionList.Group>
          </>
        )}
        {(hasBlocking || hasInaccessibleBlockingIssues) && (
          <>
            {(parent || hasBlockedBy || hasInaccessibleBlockedByIssues) && (
              <ActionList.Divider className={styles.ActionList_Divider} />
            )}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
                {LABELS.relationNames.blockingIssues}
                {issueDependenciesSummary.blocking > 0 && (
                  <CounterLabel className={styles.RelationshipCountLabel} variant="secondary">
                    {issueDependenciesSummary.blocking}
                  </CounterLabel>
                )}
              </ActionList.GroupHeading>
              {hasBlocking &&
                topBlocking.nodes.map(blockingIssue =>
                  blockingIssue ? (
                    <DependencyIssue key={blockingIssue.id} onLinkClick={onLinkClick} issue={blockingIssue} />
                  ) : null,
                )}
              {hasInaccessibleBlockingIssues && (
                <ActionList.Item disabled aria-disabled="true">
                  <ActionList.LeadingVisual>
                    <LockIcon size={16} />
                  </ActionList.LeadingVisual>
                  <span className="text-small fgColor-muted" data-testid="private-blocking-notice">
                    Private issues are hidden.
                  </span>
                </ActionList.Item>
              )}
              {hasViewAllBlocking && (
                <RelationshipsViewAllButton
                  dialogTitle={LABELS.relationNames.blockingIssues}
                  count={issueDependenciesSummary.blocking}
                >
                  <LazyRelationshipsBlockingListView
                    owner={owner}
                    repo={repo}
                    issueNumber={number}
                    blockingCount={issueDependenciesSummary.blocking}
                  />
                </RelationshipsViewAllButton>
              )}
            </ActionList.Group>
          </>
        )}
        {hasRelatesTo && (
          <RelatesToGroup
            topRelatesTo={topRelatesTo}
            owner={owner}
            repo={repo}
            issueNumber={number}
            relatesToCount={relatesToCount}
            showDivider={hasBlockingRelationships}
            onLinkClick={onLinkClick}
          />
        )}
        {hasSecurityAlerts && (
          <>
            {showDividerForSecurityAlerts && <ActionList.Divider className={styles.ActionList_Divider} />}
            <ActionList.Group>
              <ActionList.GroupHeading as="h4" className={styles.ActionList_GroupHeading}>
                {LABELS.relationNames.securityAlerts}
                {securityAlerts && securityAlerts.nodes.length > 0 && (
                  <CounterLabel className={styles.RelationshipCountLabel} variant="secondary">
                    {securityAlerts.nodes.length}
                  </CounterLabel>
                )}
              </ActionList.GroupHeading>
              {hasSecurityAlerts &&
                securityAlerts.nodes
                  .filter(a => !!a)
                  .slice(0, VISIBLE_ALERTS_THRESHOLD)
                  .map(a => (
                    <SecurityAlert
                      key={`${a.repository?.nameWithOwner}-${a.number}`}
                      securityAlert={a as SecurityAlert}
                      onLinkClick={onLinkClick}
                    />
                  ))}
              {hasViewAllSecurityAlerts && (
                <RelationshipsViewAllButton
                  dialogTitle={LABELS.relationNames.securityAlerts}
                  count={securityAlerts?.nodes.length}
                >
                  <RelationshipsSecurityAlertsListView securityAlerts={securityAlerts?.nodes} />
                </RelationshipsViewAllButton>
              )}
            </ActionList.Group>
          </>
        )}
      </ActionList>
    </Section>
  )
}
